import ContentStatusBadge from 'components/badge/ContentStatusBadge';
import ContentTypeBadge from 'components/badge/ContentTypeBadge';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { AppHelper } from 'helpers/appHelper';
import { ContentHelper } from 'helpers/contentHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';
import { ContentSearchItemType } from 'types/api/contentSearchResponseType';
import WsModalCreateContent from './component/modal/WsModalCreateContent';
import WsContentSearchEmpty from './component/pages/contentSearch/WsContentSearchEmpty';

/**
 *　コンテンツ画面
 */
export default function WsContentSearch(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { pageNo } = useParams();

  const [acctState] = useRecoilState(AcctState);
  const [modalCreateContentOpenState, setModalCreateContentOpenState] = useState(false);

  const { noticeMessageSuccess, confirm } = useAppState();
  const { postContentSearch, postQuizCopy, postDiagnosisCopy } = useApiState();

  const [processing, setProcessing] = useState(false);
  const [pageNoState, setPageNoState] = useState(1);
  const [contentsState, setContentsState] = useState<ContentSearchItemType[]>([]);
  const [hasNextState, setHasNextState] = useState(false);
  const pageLimit = 10;

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    // 認証済の場合は、会社登録画面に遷移
    const fetchData = async () => {
      try {
        await _search(pageNo);
      } catch (e) {
        showBoundary(e);
      }
    };

    fetchData();
  }, [pageNo]);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * ページングの前へボタン押下時の処理
   */
  const onPrev = async () => {
    if (pageNoState <= 1) {
      return;
    }
    if (pageNoState == 2) {
      navigate(`/ws/${acctState.company.cd}/content/search`);
    } else {
      navigate(`/ws/${acctState.company.cd}/content/search/${pageNoState - 1}`);
    }
  };

  /**
   * ページングの前へボタン押下時の処理
   */
  const onNext = async () => {
    if (!hasNextState) {
      return;
    }
    navigate(`/ws/${acctState.company.cd}/content/search/${pageNoState + 1}`);
  };

  /**
   * 詳細ボタン押下時の処理
   */
  const onEdit = async (content: ContentSearchItemType) => {
    navigate(
      `/ws/${acctState.company.cd}/${ContentHelper.getContentsTypeStr(
        content.contentsTypeDiv
      )}/edit/${content.cd}`
    );
  };

  /**
   * レポート詳細ボタン押下時の処理
   */
  const onReport = async (content: ContentSearchItemType) => {
    navigate(
      `/ws/${acctState.company.cd}/report/${ContentHelper.getContentsTypeStr(
        content.contentsTypeDiv
      )}/${content.cd}`
    );
  };

  /**
   * 複製ボタン押下時の処理
   */
  const onCopy = async (content: ContentSearchItemType) => {
    confirm({
      title: `${ContentHelper.getContentsTypeStrJpn(content.contentsTypeDiv)}の複製`,
      message: `「${content.title}」を複製してもよろしいですか？`,
      onSubmit: async () => {
        try {
          // ボタンローディングの表示
          setProcessing(true);
          switch (content.contentsTypeDiv) {
            case DivConst.items.contentsType.quiz.value:
              // クイズ複製APIの呼び出し
              await postQuizCopy({
                quizId: content.id,
              });
              break;
            case DivConst.items.contentsType.diagnosis.value:
              // 診断複製APIの呼び出し
              await postDiagnosisCopy({
                diagnosisId: content.id,
              });
              break;
            default:
              break;
          }
          // 完了メッセージの表示
          noticeMessageSuccess(
            `${ContentHelper.getContentsTypeStrJpn(content.contentsTypeDiv)}の複製が完了しました。`
          );
        } catch (e) {
          showBoundary(e);
        } finally {
          setProcessing(false);
          // 画面遷移
          await _search(pageNo);
        }
      },
    });
  };

  /**
   * コンテンツ作成モーダルを開く
   */
  const onModalCreateContentOpen = () => {
    setModalCreateContentOpenState(true);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * コンテンツ情報を検索します。
   */
  const _search = async (pageNo: string) => {
    let no = pageNo ? parseInt(pageNo) : 1;
    if (no < 1) no = 1;

    // ローディングの表示
    setProcessing(true);

    // コンテンツ検索APIの呼び出し
    const response = await postContentSearch({
      companyId: acctState.company.id,
      limit: pageLimit,
      offset: (no - 1) * pageLimit,
    });
    // 検索結果の設定
    setContentsState(response.contents);
    setPageNoState(no);
    setHasNextState(response.hasNext);

    // ローディングの停止
    setProcessing(false);
  };
  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* コンテンツ未登録の場合の表示 */}
      {(acctState.activeContent == null || acctState.activeContent.contentCount == 0) && (
        <WsContentSearchEmpty />
      )}
      {/* コンテンツ登録がある場合の表示 */}
      {acctState.activeContent?.contentCount > 0 && (
        <>
          {/* ローディング中の表示 */}
          <IndicatorScreen visible={processing} />

          <section className="section-header">
            <div className="container">
              <div className="d-md-flex d-flex-header">
                <div className="flex-shrink-0 flex-left">
                  <h2 className="flex-title">コンテンツ</h2>
                </div>
                <div className="flex-grow-1 flex-right">
                  <ul className="list-inline list-btn mb-0">
                    <li className="list-inline-item">
                      <a
                        onClick={onModalCreateContentOpen}
                        className="btn btn-common btn-common-w238 outline-blue"
                      >
                        + 新しいコンテンツを作成する
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {AppHelper.isNotEmpty(contentsState) && (
            <section className="section-report">
              <div className="container">
                <div className="box-common box-common-lg">
                  <div className="table-responsive js-scrollable">
                    <table
                      className="table table-top-gray table-top-gray-list contents"
                      style={{ minWidth: 'auto' }}
                    >
                      <thead>
                        <tr>
                          <th className="col-1 w-224">コンテンツタイトル</th>
                          <th className="col-2">
                            日付
                            <span>▲</span>
                          </th>
                          <th className="col-4" style={{ width: '250px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {contentsState.map(content => {
                          return (
                            <tr key={`ws-quiz-search-${content.id}`}>
                              <th>
                                <div className="d-flex d-flex-table">
                                  {/* コンテンツ画像の表示 */}
                                  <div className="flex-shrink-0 flex-img">
                                    <img
                                      src={AppHelper.getContentImageUrl(content.imageKey)}
                                      className="img-fluid"
                                    />
                                  </div>
                                  {/* コンテンツタイトルの表示 */}
                                  <div className="flex-grow-1 flex-text justify-content-start">
                                    <a
                                      onClick={() => onEdit(content)}
                                      className="detail hover quiz-title mb-1"
                                    >
                                      {content.title}
                                    </a>
                                    <span>
                                      <ContentTypeBadge contentsTypeDiv={content.contentsTypeDiv} />
                                    </span>
                                  </div>
                                </div>
                              </th>
                              <td>
                                {/* 登録日時の表示 */}
                                <p className="date-time mb-1">
                                  {AppHelper.formatTimeToDate(
                                    content.createdAt,
                                    'yyyy年M月dd日 h:mm a'
                                  )}
                                </p>
                                {/* コンテンツステータスの表示 */}
                                <ContentStatusBadge
                                  statusDiv={content.statusDiv}
                                  contentsTypeDiv={content.contentsTypeDiv}
                                />
                              </td>
                              <td>
                                <div className="d-flex">
                                  <div className="flex-grow-1 item-button">
                                    <ul className="list-unstyled mb-0">
                                      <li>
                                        <button
                                          onClick={() => onEdit(content)}
                                          className="btn btn-common btn-common-w110 outline-blue"
                                        >
                                          詳細
                                        </button>
                                      </li>
                                      <li>
                                        <button
                                          onClick={() => onReport(content)}
                                          className="btn btn-common btn-common-w110 outline-blue"
                                          disabled={content.publishedAt == null}
                                        >
                                          レポート
                                        </button>
                                      </li>
                                      <li>
                                        <button
                                          onClick={() => onCopy(content)}
                                          className="btn btn-common btn-common-w110 outline-blue"
                                        >
                                          複製
                                        </button>
                                      </li>
                                      <li>
                                        <button
                                          onClick={() =>
                                            content.lineFlag
                                              ? ContentHelper.openLineProductionUrl(
                                                  content.lineCd,
                                                  content.contentsTypeDiv
                                                )
                                              : ContentHelper.openProductionUrl(
                                                  content.cd,
                                                  content.contentsTypeDiv
                                                )
                                          }
                                          className="btn btn-common btn-common-w110 outline-blue"
                                          disabled={
                                            content.statusDiv !==
                                            DivConst.items.quizStatus.published.value
                                          }
                                        >
                                          公開URL
                                        </button>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* ページングボタン */}
                  <nav
                    aria-label="Page navigation"
                    className="navigation mt-4"
                    style={{ position: 'static' }}
                  >
                    <ul className="pagination mb-0">
                      <li className="page-item">
                        <a
                          onClick={onPrev}
                          className={`page-link prev ${pageNoState <= 1 && `disabled`}`}
                          aria-label="Previous"
                        >
                          <span aria-hidden="true"></span>
                        </a>
                      </li>
                      <li className="page-item">
                        <a
                          onClick={onNext}
                          className={`page-link next ${!hasNextState && `disabled`}`}
                          aria-label="Next"
                        >
                          <span aria-hidden="true"></span>
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </section>
          )}
        </>
      )}
      {/* コンテンツ作成モーダル */}
      <WsModalCreateContent
        open={modalCreateContentOpenState}
        setOpen={setModalCreateContentOpenState}
      />
    </>
  );
}
